import './styles/Watch.scss';

import { useFormik } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn, tryCatch } from '../../../../helpers';
import { parseError } from '../../../../helpers/errorUtils';
import { useClassHelper } from '../../../../hooks/useClassHelper';
import { useGoogleFonts } from '../../../../hooks/useGoogleFonts';
import { WebsheetTemplateProps } from '../../../../types';
import AppleLoading from '../../../AppleLoading';
import { Dropdown } from '../../../Dropdown/Dropdown';
import { Form } from '../../../Form';
import { Stack } from '../../../Stack';
import { UnexpectedErrorWebsheet } from '../../../UnexpectedErrorWebsheet/UnexpectedErrorWebsheet';
import { Button } from '../../components/Button';
import { DefaultErrorTemplate } from '../../components/DefaultErrorTemplate';
import { FeedbackSection } from '../../components/FeedbackSection';
import { ImageHeader } from '../../components/ImageHeader';
import { PlanInfo } from '../../components/PlanInfo';
import { DATADOG_ACTIONS } from '../../constants';
import { trackSdkCall } from '../../helpers/trackSdkCall';
import { useDataDogActions } from '../../hooks/useDataDogActions';
import { useGetStyles } from '../../hooks/useGetStyles';
import { useLocalisation } from '../../hooks/useLocalisation';
import { useVisibilityCheck } from '../../hooks/useVisibilityCheck';
import {
  componentName,
  transferErrorFields,
  transferFeedbackFields,
  transferFields,
  transferSuccessFields,
  watchErrorFieldsMessages,
  watchTransferFamilyPlanFields,
} from './constants';
import { findPlanDetails, resolveTransferText } from './helpers';
import { useFetchTransferPlan, useWatchImage } from './hooks';
import { TransferTermsConditionsW } from './TransferTermsConditions_W';

const DATA_DOG_CONTEXT = 'watch_transfer';

type TransferView =
  | { type: 'form' }
  | { type: 'terms' }
  | { type: 'success' }
  | {
      type: 'error';
      errorCode: string;
      errorMessage: string;
      subscriptionType?: string;
    };

export const WatchTransfer = (props: WebsheetTemplateProps) => {
  const {
    values,
    text = {}, // currentText from PreviewContainer.tsx
    deviceSdk,
    currentLocale,
    services,
    state,
    portalMessenger,
    isPortalPreview,
    selectedField,
    deviceType,
  } = props;

  // --- Context & services ---
  const { t } = useTranslation();

  const { isPortalTest, dataDogLogger, actions } = useDataDogActions({
    isPortalPreview,
    actionKey: DATA_DOG_CONTEXT,
  });

  const { localisations } = values;

  // --- Localisation ---
  // Always call the hook (Rules of Hooks); pass the logger only outside preview
  // so the localisation_not_found warning never fires in preview/test mode.
  const localisedWatch = useLocalisation(
    localisations,
    currentLocale,
    isPortalPreview ? undefined : dataDogLogger,
    'transfer_t&c',
  );

  const currentLocalisation = isPortalPreview ? text : localisedWatch;

  // --- Styles & layout ---
  const isPreviewLayout = isPortalPreview || isPortalTest;
  const getStyles = useGetStyles({ values });
  const c = useClassHelper(componentName);
  useGoogleFonts([values?.fontParagraph, values?.fontTitle]);

  // --- State ---
  const [view, setView] = useState<TransferView>({ type: 'form' });

  // --- Data fetching ---
  const { data, status, error } = useFetchTransferPlan({
    services,
    state,
  });

  console.log({
    sub: data?.subscription_type,
    field: selectedField?.field,
    flag: (values as any)?.supports_family_plan,
  });

  // Fallback rule: a missing attribute defaults to 'paired' so today's Multi-SIM
  // transfer copy is preserved for backward compatibility.
  const subscriptionType = data?.subscription_type ?? 'paired';

  // "linked" → sa-watch banner and sa-watch localised text
  // "paired" → Multi-SIM banner and Multi-SIM localised text
  const isLinked = subscriptionType === 'linked';

  // In portal preview there is no `subscription_type`, so the carrier previews the
  // linked (sa-watch / standalone) variant by selecting one of its localisation
  // fields.
  const isTransferFamilyPlanSelected = watchTransferFamilyPlanFields.includes(
    selectedField?.field as (typeof watchTransferFamilyPlanFields)[number],
  );
  const isLinkedView =
    isLinked || (isPortalPreview && isTransferFamilyPlanSelected);

  // Tag the RUM session with the subscription mode so every transfer event
  // (success, error, timings) is segmentable by paired vs linked. Runtime only.
  useEffect(() => {
    if (isPortalPreview || !data) {
      return;
    }
    dataDogLogger.addGlobal('subscription_type', subscriptionType);
  }, [data, isPortalPreview, subscriptionType, dataDogLogger]);

  const { headerImageUrl, imageConfig } = useWatchImage(
    values,
    deviceType,
    isLinkedView,
  );

  // Ref on the main content render. checkPresence is off so the other view
  // branches (terms/error/feedback) — where this ref is null — stay no-ops
  // instead of firing a false MISSING_DOM.
  const containerRef = useRef<HTMLElement>(null);

  useVisibilityCheck(containerRef, {
    enabled: !isPortalPreview,
    checkPresence: false,
    logger: dataDogLogger,
    errorAction: DATADOG_ACTIONS.errors.template_error,
    flow: 'render',
  });

  const handleImageError = (imageUrl: string) => {
    if (isPortalPreview) {
      return;
    }

    dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
      flow: 'render_logo',
      statusCode: 0,
      errorCode: 'IMAGE_LOAD_FAILED',
      errorMessage: `Logo image failed to load: ${imageUrl}`,
      imageUrl,
    });
  };

  // --- Selected-field flags (portal preview) ---
  const isTransferFieldSelected = transferFields.includes(
    selectedField?.field as (typeof transferFields)[number],
  );

  const isFeedbackBySelectedField = transferFeedbackFields.includes(
    selectedField?.field as (typeof transferFeedbackFields)[number],
  );

  const isSuccessFieldsSelected = transferSuccessFields.includes(
    selectedField?.field as (typeof transferSuccessFields)[number],
  );

  const isErrorFieldsSelected = transferErrorFields.includes(
    selectedField?.field as (typeof transferErrorFields)[number],
  );

  // --- Derived text ---
  // Resolve all transfer copy with the linked (sa-watch) vs paired branch. Paired
  // resolves to the exact same keys as before, so Multi-SIM journeys are unchanged.
  const transferCopy = useMemo(
    () => resolveTransferText(isLinkedView, text, currentLocalisation),
    [isLinkedView, text, currentLocalisation],
  );

  const watchTransferConsentText = useMemo(() => {
    const localizedText = transferCopy.plansText
      ?.replace(
        /\{source_watch_name\}/g,
        data?.source_watch_name || 'Source Watch',
      )
      ?.replace(
        /\{target_watch_name\}/g,
        data?.target_watch_name || 'Target Watch',
      );

    return (
      localizedText ||
      `You are about to transfer your ${data?.source_watch_name} to ${data?.target_watch_name}.`
    );
  }, [transferCopy, data]);

  // --- Form ---
  const formik = useFormik({
    initialValues: {
      plan_offer_id: undefined,
    },
    onSubmit: async values => {
      // Mark the transfer start so DataDog can derive submit→success latency
      // from the view-relative custom timings.
      dataDogLogger.addTiming(DATADOG_ACTIONS.timings.transfer_submitted);

      const { error } = await tryCatch(async () => {
        setView({ type: 'form' });
        const activationData = await services.transferWatch({
          state,
        });

        await deviceSdk.transferWatch(activationData);

        if (portalMessenger) {
          portalMessenger.propagateSuccess();
        }
      });

      if (error) {
        const {
          errorCode,
          errorMessage,
          statusCode,
          subscriptionType: errorSubscriptionType,
        } = parseError(error);

        if (portalMessenger) {
          portalMessenger.propagateError();
        }

        // Skip DataDog in preview/test mode to avoid polluting production metrics
        if (!isPortalPreview) {
          dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
            flow: 'submit',
            statusCode,
            errorCode: errorCode ?? 'UNKNOWN',
            errorMessage,
          });
        }

        setView({
          type: 'error',
          errorCode: errorCode ?? 'UNKNOWN',
          errorMessage,
          // Body value wins; the flow's own subscription type covers codes
          // that omit it (e.g. SESSION_NOT_FOUND until UES-12175 lands).
          subscriptionType: errorSubscriptionType ?? subscriptionType,
        });
        return;
      }

      // --- Success path ---
      if (!isPortalPreview) {
        dataDogLogger.log(actions?.submit_success, { flow: 'submit' });

        // Mark transfer completion → (succeeded − submitted) = transfer latency.
        dataDogLogger.addTiming(DATADOG_ACTIONS.timings.transfer_succeeded);

        // Tag the RUM session as completed and emit the success marker while the
        // session is still live. stopSession is deferred to the success-screen
        // close so the feedback interactions stay in the same session.
        dataDogLogger.addGlobal('completed', true);
        dataDogLogger.log(DATADOG_ACTIONS.success.template_success, {
          flow: 'submit',
        });
      }

      setView({ type: 'success' });
    },
  });

  const { isSubmitting, handleSubmit, submitForm } = formik;

  useEffect(() => {
    if (error) {
      const {
        errorCode,
        errorMessage,
        statusCode,
        subscriptionType: errorSubscriptionType,
      } = parseError(error);

      // Skip DataDog tracking in preview/test mode to avoid polluting production metrics
      if (!isPortalPreview) {
        dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
          flow: 'load_plans',
          statusCode,
          errorCode: errorCode ?? 'UNKNOWN',
          errorMessage,
        });
      }

      setView({
        type: 'error',
        errorCode: errorCode ?? 'UNKNOWN',
        errorMessage,
        // Body value wins; the flow's own subscription type covers codes
        // that omit it (e.g. SESSION_NOT_FOUND until UES-12175 lands).
        subscriptionType: errorSubscriptionType ?? subscriptionType,
      });
    }
  }, [error]);

  // Derived flag for the success-screen effect; kept separate from the
  // render-local isSuccess which also factors in preview selected-field.
  const isSuccessView = view.type === 'success';

  // Tracks when the user actually sees the success screen, separate from
  // submit_success which fires in onSubmit when the transfer returns. A render
  // bug could set view to 'success' without FeedbackSection ever mounting —
  // this catches that gap.
  useEffect(() => {
    if (isSuccessView && !isPortalPreview) {
      dataDogLogger.log(actions?.success_screen_shown, { flow: 'submit' });
    }
  }, [isSuccessView]);

  const handleOpenTermsAndConditions = (
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    e.preventDefault();

    if (isPortalPreview) {
      return;
    }

    setView({ type: 'terms' });
  };

  if (status === 'pending') {
    return (
      <div className={c('__loading-wrapper')}>
        <AppleLoading />
      </div>
    );
  }

  if (view.type === 'terms' || isTransferFieldSelected) {
    return (
      <TransferTermsConditionsW
        {...props}
        isLinked={isLinkedView}
        onClose={() => {
          if (isPortalPreview) {
            return;
          }

          setView({ type: 'form' });
        }}
        isPortalPreview={isPortalPreview}
        data-dd-action-name={actions?.terms_and_conditions_closed}
      />
    );
  }

  if (view.type === 'error' || isErrorFieldsSelected) {
    const viewErrorCode = view.type === 'error' ? view.errorCode : '';

    const isErrorMessageSelected = watchErrorFieldsMessages.includes(
      selectedField?.field,
    );

    const errorCode =
      isPortalPreview && selectedField?.field && isErrorMessageSelected
        ? selectedField?.field
        : viewErrorCode;

    // Runtime: the error's own subscription_type decides the copy side.
    // Preview: selecting any family_plan_ transfer field flips to the linked
    // variant.
    const isLinkedError =
      view.type === 'error' && !isPortalPreview
        ? (view.subscriptionType ?? subscriptionType) === 'linked'
        : isLinkedView;

    const errorTitle =
      (isLinkedError
        ? ((currentLocalisation?.family_plan_error_title ||
            text?.family_plan_error_title) as string)
        : '') ||
      currentLocalisation?.error_title ||
      text?.error_title ||
      '';

    if (errorCode === 'INVALID_STATE') {
      return (
        <UnexpectedErrorWebsheet
          title={t('error.title')}
          body={t('error.body')}
          close={t('error.close')}
          onClick={() => {
            if (deviceSdk && deviceSdk.exit) {
              // exit() emits finishFlow + dismissFlow actions; stop the session
              // after so those logs are captured in it.
              trackSdkCall('exit', () => deviceSdk.exit(), dataDogLogger);
              dataDogLogger.stopSession();
              return;
            }

            // SDK method missing — the user's close does nothing. Report the
            // silent failure, then close the session.
            if (!isPortalPreview) {
              dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
                flow: 'sdk_unavailable',
                errorCode: 'SDK_METHOD_MISSING',
                errorMessage: 'deviceSdk.exit is unavailable',
                method: 'exit',
              });
              dataDogLogger.stopSession();
            }
          }}
        />
      );
    }

    return (
      <DefaultErrorTemplate
        title={errorTitle}
        closeButtonText={text?.close_button}
        baseComponentName={componentName}
        style={getStyles as Record<string, string | number>}
        errorCode={errorCode}
        isPortalPreview={isPortalPreview}
        isLinked={isLinkedError}
        localisation={{ ...text, ...currentLocalisation }}
        data-dd-action-name={actions?.error_message_closed}
        onClick={async () => {
          if (isPortalPreview) {
            return;
          }

          if (portalMessenger) {
            portalMessenger.propagateError();
          }

          try {
            await deviceSdk.decline();
          } catch (declineError) {
            const { errorCode, errorMessage, statusCode } =
              parseError(declineError);

            dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
              flow: 'decline',
              statusCode,
              errorCode: errorCode ?? 'UNKNOWN',
              errorMessage,
            });
          } finally {
            // decline() emits its own dismissFlow action; stop the session
            // after so that log is captured in it.
            dataDogLogger.stopSession();
          }
        }}
      />
    );
  }

  if (status === 'success') {
    if (isSubmitting || view.type === 'success' || isFeedbackBySelectedField) {
      const isSuccess = isFeedbackBySelectedField
        ? isSuccessFieldsSelected
        : view.type === 'success';
      return (
        <FeedbackSection
          componentName={componentName}
          style={getStyles}
          isSubmitting={!isSuccess}
          onClick={() => {
            if (isPortalPreview) {
              return;
            }

            // Let close() emit its own finishFlow action first, then stop the
            // session so that log lands in it. The native teardown is posted
            // async, so this synchronous tick completes before the webview dies.
            trackSdkCall('close', () => deviceSdk.close(), dataDogLogger);
            dataDogLogger.stopSession();
          }}
          text={{
            submitting_title:
              transferCopy.submittingTitle ||
              'Transferring Watch Sidekick Plan',
            success_title:
              transferCopy.successTitle || 'Watch Sidekick Plan Transferred',
            // Transfer collects its own close copy under Success States;
            // fall back to the watch-tab close for configs saved before the
            // field existed.
            close_button:
              transferCopy.closeButton || text?.close_button || 'Close',
            submitting_paragraph:
              transferCopy.submittingParagraph || 'Transferring your plan...',
            success_paragraph:
              transferCopy.successParagraph ||
              'Plan transferred successfully. Your watch is now ready to use.',
          }}
          selectedField={selectedField}
          isPortalPreview={isPortalPreview}
          data-dd-action-name={actions?.feedback_section_closed}
        />
      );
    }

    const planDetails = findPlanDetails(data?.plan, currentLocale);

    console.log(transferCopy);

    return (
      <main ref={containerRef} className={c('base')} style={getStyles}>
        <Stack
          direction="column"
          className={c('__inner', { '__inner-preview': isPreviewLayout })}
        >
          {Boolean(headerImageUrl) && (
            <ImageHeader
              url={headerImageUrl as string}
              alt="watch"
              isPortalPreview={isPreviewLayout}
              imageConfig={imageConfig}
              onError={handleImageError}
            />
          )}

          <section
            className={cn('flex-1 shrink basis-0 pt-5 pb-6 px-4', {
              'pb-[36px]': isPortalPreview,
            })}
          >
            <Form
              loading={isSubmitting}
              onSubmit={event => {
                event.preventDefault();

                if (isPortalPreview) {
                  return;
                }

                handleSubmit(event);
              }}
              className={c('__inner-form')}
            >
              <Stack direction="column" gap={4} justify="between" height="full">
                <Stack
                  direction="column"
                  className={cn('gap-4', {
                    'gap-[13px]!': isPreviewLayout,
                  })}
                >
                  <h1
                    className={c('__title', {
                      '__title--spacing': !headerImageUrl,
                      '__title--is-preview': isPreviewLayout,
                    })}
                  >
                    {transferCopy.title ?? 'Transfer Watch Plan'}{' '}
                  </h1>

                  <p className={c('__text')}>
                    <Dropdown.GoogleFont.Viewer
                      fontFamily={values?.fontParagraph}
                    >
                      {watchTransferConsentText}
                    </Dropdown.GoogleFont.Viewer>
                  </p>
                  {data?.plan && (
                    <PlanInfo
                      name={planDetails?.name}
                      description={planDetails?.description}
                      descriptionExtended={planDetails?.description_extended}
                      hideDescriptionExtended={Boolean(
                        transferCopy.disablePlanDescription,
                      )}
                    />
                  )}
                </Stack>

                <Stack
                  direction="column"
                  gap={isPreviewLayout ? 2 : 4}
                  justify="center"
                >
                  <Button
                    type="submit"
                    data-dd-action-name={actions?.submit_button_clicked}
                  >
                    {transferCopy.continueButton || 'Continue'}
                  </Button>
                  {data?.requires_tc && (
                    <div
                      className={c('__terms', {
                        '__terms-preview': isPreviewLayout,
                      })}
                    >
                      <Dropdown.GoogleFont.Viewer
                        fontFamily={values?.fontParagraph}
                      >
                        {transferCopy.checkboxLabel ||
                          'By continuing you agree to accept the '}{' '}
                        <a
                          href="/terms-and-conditions"
                          onClick={handleOpenTermsAndConditions}
                          className={c('__link')}
                          data-dd-action-name={
                            actions?.open_terms_and_conditions_clicked
                          }
                        >
                          {transferCopy.link || 'Terms & Conditions'}
                        </a>
                      </Dropdown.GoogleFont.Viewer>
                    </div>
                  )}
                </Stack>
              </Stack>
            </Form>
          </section>
        </Stack>
      </main>
    );
  }

  return null;
};
